import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Button,
  Alert,
  Chip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDetectDataTypesMutation } from "../../../features/import/importApiSlice";

const detectCache = {
  key: null,
  detected: null,
};

function DetectOptionalData({ parsedData, sourceType, onDetected }) {
  const [detectTypes, { isLoading }] = useDetectDataTypesMutation();
  const [detectedTypes, setDetectedTypes] = useState({
    hasContracts: false,
    hasEvents: false,
    hasBookings: false,
    hasSiteManagers: false,
  });
  const [autoDetected, setAutoDetected] = useState(false);
  const lastDetectKeyRef = useRef(null);

  const handleAutoDetect = useCallback(async () => {
    try {
      const result = await detectTypes({ parsedData, sourceType }).unwrap();
      setDetectedTypes(result.detected);
      detectCache.key = `${sourceType}:${parsedData?.headers?.join("|") || ""}:${
        parsedData?.rows?.length || parsedData?.mitglieder?.length || 0
      }`;
      detectCache.detected = result.detected;
      setAutoDetected(true);
    } catch (err) {
      console.error("Detection error:", err);
      // Fall back to manual selection
      setAutoDetected(true);
    }
  }, [detectTypes, parsedData, sourceType]);

  const detectKey = useMemo(() => {
    const headersKey = parsedData?.headers?.join("|") || "";
    const rowCount = parsedData?.rows?.length || parsedData?.mitglieder?.length || 0;
    return `${sourceType}:${headersKey}:${rowCount}`;
  }, [
    sourceType,
    parsedData?.headers,
    parsedData?.rows?.length,
    parsedData?.mitglieder?.length,
  ]);

  useEffect(() => {
    // Auto-detect on mount or when source changes
    if (lastDetectKeyRef.current === detectKey) {
      return;
    }
    if (detectCache.key === detectKey && detectCache.detected) {
      setDetectedTypes(detectCache.detected);
      setAutoDetected(true);
      lastDetectKeyRef.current = detectKey;
      return;
    }
    lastDetectKeyRef.current = detectKey;
    handleAutoDetect();
  }, [handleAutoDetect, detectKey]);

  const handleToggle = (field) => {
    setDetectedTypes((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleContinue = () => {
    onDetected(detectedTypes);
  };

  const dataTypes = [
    {
      key: "hasContracts",
      icon: "mdi:file-document",
      label: "Verträge",
      description: "Vertragsnehmer mit Bankdaten und Zahlungsinformationen",
      color: "#1976d2",
    },
    {
      key: "hasEvents",
      icon: "mdi:calendar-star",
      label: "Veranstaltungen",
      description: "Prüfungen, Lehrgänge und andere Events",
      color: "#ed6c02",
    },
    {
      key: "hasBookings",
      icon: "mdi:cash-register",
      label: "Buchungen",
      description: "Historische Abrechnungen und Transaktionen",
      color: "#2e7d32",
    },
    {
      key: "hasSiteManagers",
      icon: "mdi:account-supervisor",
      label: "Standortleiter",
      description: "Verantwortliche Personen für einzelne Standorte",
      color: "#9c27b0",
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Optionale Daten erkennen
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        Welche zusätzlichen Daten enthält Ihre Datei?
      </Typography>

      {!autoDetected && isLoading && (
        <Alert
          severity="info"
          icon={<Icon icon="mdi:loading" className="rotating" />}
        >
          <Typography variant="body2">
            Ihre Datei wird analysiert, um optionale Daten zu erkennen...
          </Typography>
        </Alert>
      )}

      {autoDetected && (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              Automatische Erkennung abgeschlossen
            </Typography>
            <Typography variant="caption">
              Wir haben {Object.values(detectedTypes).filter(Boolean).length}{" "}
              optionale Datentypen erkannt. Sie können die Auswahl unten
              anpassen.
            </Typography>
          </Alert>

          <FormGroup>
            {dataTypes.map((type) => (
              <Paper
                key={type.key}
                elevation={detectedTypes[type.key] ? 3 : 1}
                sx={{
                  p: 2,
                  mb: 2,
                  border: 2,
                  borderColor: detectedTypes[type.key]
                    ? type.color
                    : "transparent",
                  transition: "all 0.3s ease",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: `${type.color}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      icon={type.icon}
                      width={24}
                      style={{ color: type.color }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6">{type.label}</Typography>
                      {detectedTypes[type.key] && (
                        <Chip
                          label="Erkannt"
                          size="small"
                          color="success"
                          icon={<Icon icon="mdi:check" />}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={detectedTypes[type.key]}
                        onChange={() => handleToggle(type.key)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </Paper>
            ))}
          </FormGroup>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Hinweis:</strong> Der Import von Mitgliedern und
              Standorten ist immer enthalten. Wählen Sie hier nur zusätzliche
              Datentypen aus.
            </Typography>
          </Alert>

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              endIcon={<Icon icon="mdi:arrow-right" />}
            >
              Weiter mit ausgewählten Daten
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

DetectOptionalData.propTypes = {
  parsedData: PropTypes.object.isRequired,
  sourceType: PropTypes.string.isRequired,
  onDetected: PropTypes.func.isRequired,
};

export default DetectOptionalData;
